import type { ReactNode } from 'react';
import { AdminPaymentDetail } from '../../services/paymentsApi';

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="admin-field">
      <div className="admin-field-label">{label}</div>
      <div className="admin-field-value">{value ?? '—'}</div>
    </div>
  );
}

const PROVIDER_LABELS: Record<string, string> = { stripe: 'Stripe', paymob: 'Paymob', mock: 'Mock', tabby: 'Tabby' };

/** Spec 067 — who pays the Tabby fee on this payment. */
const FEE_BEARER_LABELS: Record<string, string> = { GARAGE: 'Garage', GARAGAT: 'Garagat' };

function ProviderBadge({ provider }: { provider: string }) {
  const key = provider.toLowerCase();
  return (
    <span className={`admin-badge payment-provider ${key}`} data-testid="payment-provider">
      {PROVIDER_LABELS[key] ?? provider}
    </span>
  );
}

interface PaymentBlockProps {
  paymentId: string | null;
  payment: AdminPaymentDetail | null;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
}

/**
 * Spec 051 AC-20 + Spec 052 — provider-aware payment summary on the admin order detail.
 * Data is fetched by the page (alongside the disputes) and passed in. If billing is
 * unreachable the card degrades to the internal payment UUID plus a retry link.
 */
export default function PaymentBlock({ paymentId, payment, loading, failed, onRetry }: PaymentBlockProps) {
  const mono = { fontFamily: 'monospace', fontSize: '0.8rem' } as const;
  const gatewayRef = payment?.gatewayTransactionId ?? payment?.paymobTransactionId ?? null;
  // Spec 067 — for Tabby the gateway reference IS the Tabby payment id (also mirrored in gatewayTransactionId).
  const isTabby = payment?.provider?.toLowerCase() === 'tabby';
  const tabbyPaymentId = isTabby ? payment?.gatewayReference ?? payment?.gatewayTransactionId ?? null : null;

  return (
    <div className="admin-card" data-testid="payment-block">
      <div className="payment-block-header">
        <h3 className="admin-card-title" style={{ margin: 0 }}>Payment</h3>
        {payment && <ProviderBadge provider={payment.provider} />}
      </div>
      {!paymentId ? (
        <div className="admin-empty-state"><p>No payment recorded for this order</p></div>
      ) : loading ? (
        <p style={{ fontSize: '0.875rem', color: 'var(--admin-text-muted)', margin: 0 }}>
          Loading payment details...
        </p>
      ) : failed || !payment ? (
        <div className="admin-detail-grid">
          <Field label="Payment ID" value={<span style={mono}>{paymentId}</span>} />
          <div className="admin-field">
            <div className="admin-field-label">Details</div>
            <div className="admin-field-value" style={{ color: 'var(--admin-text-muted)' }}>
              Payment details unavailable{' '}
              <button type="button" className="invoice-link-btn" onClick={onRetry}>
                Retry
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="admin-detail-grid">
          <Field label="Payment date" value={new Date(payment.createdAt).toLocaleString()} />
          <Field label="Method" value={payment.paymentMethodUsed} />
          <Field
            label="Amount"
            value={
              <>
                {payment.amount.toFixed(2)} {payment.currency}
                {/* Spec 068 V10 — only when the payment carries Garagat's service fee (> 0). */}
                {(payment.serviceFeeGross ?? 0) > 0 && (
                  <div data-testid="payment-service-fee-line" style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>
                    incl. service fee {(payment.serviceFeeGross as number).toFixed(2)} {payment.currency}
                  </div>
                )}
              </>
            }
          />
          <Field
            label="Gateway fee"
            value={
              payment.feeAmount == null ? null : (
                <>
                  {payment.feeAmount.toFixed(2)} {payment.currency}
                  {!payment.feeIsActual && (
                    <span className="fee-estimate-chip" title="Schedule estimate — the provider has not reported the actual fee yet">
                      estimated
                    </span>
                  )}
                  {payment.feeTaxAmount != null && (
                    <div
                      data-testid="fee-vat-line"
                      style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}
                      title="VAT the provider charged on its fee (Spec 053 AC-13) — recoverable input VAT"
                    >
                      of which VAT {payment.feeTaxAmount.toFixed(2)} {payment.currency}
                    </div>
                  )}
                </>
              )
            }
          />
          <Field label="Net" value={payment.netAmount == null ? null : `${payment.netAmount.toFixed(2)} ${payment.currency}`} />
          <Field
            label="Max refundable"
            value={payment.maxRefundable == null ? null : `${payment.maxRefundable.toFixed(2)} ${payment.currency}`}
          />
          {/* #525 — settled refunds only; a refund still waiting on the gateway shows up here when it completes. */}
          <Field
            label="Refunded"
            value={!payment.refundedAmount ? null : `${payment.refundedAmount.toFixed(2)} ${payment.currency}`}
          />
          {isTabby ? (
            <>
              <Field label="Tabby payment ID" value={tabbyPaymentId ? <span style={mono} data-testid="tabby-payment-id">{tabbyPaymentId}</span> : null} />
              <Field label="Tabby fee paid by" value={payment.feeBearer ? FEE_BEARER_LABELS[payment.feeBearer] ?? payment.feeBearer : null} />
              <Field
                label="Paid out to garage from"
                value={payment.settlementEligibleAt ? new Date(payment.settlementEligibleAt).toLocaleDateString() : null}
              />
            </>
          ) : (
            <Field label="Gateway reference" value={gatewayRef ? <span style={mono}>{gatewayRef}</span> : null} />
          )}
          <Field label="Transaction ref" value={payment.transactionRef ? <span style={mono}>{payment.transactionRef}</span> : null} />
          <Field label="Status" value={payment.status} />
        </div>
      )}
    </div>
  );
}
